/**
 * Audio Player Module
 * Manages the custom audio player controls and event handlers
 */

import appState from '../../core/app-state.js';
import logger from '../../core/logger.js';
import EventLogger, { logEvent } from '../../core/event-logger.js';
import { EVENT_TYPES } from '../../core/event-types.js';
import { REWIND_SECONDS, FIRST_LISTEN_END_THRESHOLD } from '../../config/constants.js';
import { formatTime } from '../../utils/time-utils.js';
import { updatePlaybackProgressBar, startProgressAnimation, stopProgressAnimation } from './progress-bar.js';
import { trackDepthMetrics, trackDepthSeek } from './depth-metrics.js';
import { revealQuestions } from '../questions/pre-questions-card.js';

// Extended HTMLAudioElement with custom properties
interface ExtendedHTMLAudioElement extends HTMLAudioElement {
  _playerEventListeners?: Record<string, EventListener>;
}

// Global flag to track if keyboard handler is attached (shared across player instances)
let globalSpaceHandlerAttached = false;

/**
 * Check if first listen is complete and trigger question reveal if so
 * @param audioEl - The audio element
 */
function checkFirstListenCompletion(audioEl: HTMLAudioElement): void {
  if (!appState.activeClipId) return;
  
  // Skip if already marked as complete
  if (appState.firstListenComplete[appState.activeClipId]) return;
  
  // Check if audio has reached near the end
  const isNearEnd = audioEl.currentTime >= audioEl.duration - FIRST_LISTEN_END_THRESHOLD;
  
  // Only trigger if audio is playing and near the end
  if (isNearEnd && !audioEl.paused && audioEl.duration > 0) {
    // Mark as complete
    appState.firstListenComplete[appState.activeClipId] = true;
    
    // Log event
    logEvent(EVENT_TYPES['PLAYBACK_FIRST_LISTEN_COMPLETE']!, {
      clipId: appState.activeClipId,
      duration: audioEl.duration
    });
    
    // Reveal questions if pre-questions card is present
    const preQuestionsCard = document.querySelector('.pre-questions-card');
    if (preQuestionsCard) {
      revealQuestions();
    }
  }
}


/**
 * Initialize custom audio player with rewind/play/forward controls
 */
export function initCustomPlayer(audioEl: HTMLAudioElement, containerEl: HTMLElement): void {
  // Store event listeners to clean up later
  (audioEl as ExtendedHTMLAudioElement)._playerEventListeners = {};
  
  // Store audio element reference in appState
  appState.refs.audioEl = audioEl;
  
  // Remove audio-disabled class if present from previous clips
  const existingDisabledControls = document.querySelectorAll('.audio-controls.audio-disabled');
  existingDisabledControls.forEach(el => el.classList.remove('audio-disabled'));
  
  // Get player elements
  const playPauseBtn = document.getElementById('playPauseBtn');
  const rewindBtn = document.getElementById('rewindBtn');
  const elapsedTime = document.getElementById('elapsedTime');
  const remainingTime = document.getElementById('remainingTime');
  
  if (!playPauseBtn || !rewindBtn || !elapsedTime || !remainingTime) {
    logger.error('Required player elements not found');
    return;
  }
  
  // Function to update play/pause button state
  function updatePlayPauseState(isPlaying: boolean): void {
    if (isPlaying) {
      playPauseBtn!.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="player-icon">
        <path fill-rule="evenodd" d="M6.75 5.25a.75.75 0 01.75-.75H9a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H7.5a.75.75 0 01-.75-.75V5.25zm7.5 0A.75.75 0 0115 4.5h1.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H15a.75.75 0 01-.75-.75V5.25z" clip-rule="evenodd" />
      </svg>`;
      playPauseBtn!.setAttribute('aria-label', 'Pause');
      playPauseBtn!.setAttribute('aria-pressed', 'true');
    } else {
      playPauseBtn!.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="player-icon">
        <path fill-rule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.715 1.295 2.567 0 3.283L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653z" clip-rule="evenodd" />
      </svg>`;
      playPauseBtn!.setAttribute('aria-label', 'Play');
      playPauseBtn!.setAttribute('aria-pressed', 'false');
    }
  }
  
  // Play/pause button click handler
  const playPauseHandler = () => {
    if (audioEl.paused) {
      audioEl.play().catch(error => {
        logger.warn('Audio playback failed', error);
      });
      
      // Log playback_play event
      logEvent(EVENT_TYPES['PLAYBACK_PLAY']!, { 
        clipId: appState.activeClipId,
        currentTime: audioEl.currentTime
      });
      
      // Log clip_started only for first play of this clip
      if (appState.activeClipId && !EventLogger.hasClipStarted(appState.activeClipId)) {
        const clip = appState.clips.find(c => c.id === appState.activeClipId);
        const fromSource = appState.dailySet && appState.dailySet.includes(appState.activeClipId!) ? 'dailySet' : 
                          (clip && clip.storyId ? 'series' : 'library');
        
        logEvent(EVENT_TYPES['CLIP_STARTED']!, { 
          clipId: appState.activeClipId,
          from: fromSource,
          difficulty: clip ? clip.difficulty : null,
          seriesId: clip ? clip.storyId : null
        });
        EventLogger.markClipStarted(appState.activeClipId);
      }
    } else {
      audioEl.pause();
      
      // Log playback_pause event
      logEvent(EVENT_TYPES['PLAYBACK_PAUSE']!, { 
        clipId: appState.activeClipId,
        currentTime: audioEl.currentTime
      });
    }
  };
  playPauseBtn.addEventListener('click', playPauseHandler);
  
  // Rewind button click handler
  const rewindHandler = () => {
    const previousTime = audioEl.currentTime;
    audioEl.currentTime = Math.max(audioEl.currentTime - REWIND_SECONDS, 0);
    
    // Track seek for depth metrics
    trackDepthSeek(audioEl, previousTime, audioEl.currentTime);
    
    // Log playback_seek event
    logEvent(EVENT_TYPES['PLAYBACK_SEEK']!, { 
      clipId: appState.activeClipId,
      fromTime: previousTime,
      toTime: audioEl.currentTime,
      seekType: 'rewind_10'
    });
    
    // Also log rewind_10 event for symmetry
    logEvent(EVENT_TYPES['PLAYBACK_REWIND_10']!, {
      clipId: appState.activeClipId,
      fromTime: previousTime,
      toTime: audioEl.currentTime
    });
  };
  rewindBtn.addEventListener('click', rewindHandler);
  
  // Forward button click handler
  const forwardBtn = document.getElementById('forwardBtn');
  const forwardHandler = () => {
    const previousTime = audioEl.currentTime;
    audioEl.currentTime = Math.min(audioEl.currentTime + REWIND_SECONDS, audioEl.duration || 0);
    
    // Track seek for depth metrics
    trackDepthSeek(audioEl, previousTime, audioEl.currentTime);
    
    // Log playback_seek event
    logEvent(EVENT_TYPES['PLAYBACK_SEEK']!, { 
      clipId: appState.activeClipId,
      fromTime: previousTime,
      toTime: audioEl.currentTime,
      seekType: 'forward_10'
    });
    
    // Also log forward_10 event for symmetry
    logEvent(EVENT_TYPES['PLAYBACK_FORWARD_10']!, {
      clipId: appState.activeClipId,
      fromTime: previousTime,
      toTime: audioEl.currentTime
    });
  };
  if (forwardBtn) {
    forwardBtn.addEventListener('click', forwardHandler);
  }
  
  // Timeupdate event handler (now shows elapsed and remaining time)
  const timeUpdateHandler = () => {
    // Update elapsed time
    const currentFormatted = formatTime(audioEl.currentTime);
    elapsedTime.textContent = currentFormatted;
    
    // Update remaining time (duration - current)
    if (audioEl.duration && !isNaN(audioEl.duration)) {
      const remaining = audioEl.duration - audioEl.currentTime;
      const remainingFormatted = formatTime(remaining);
      remainingTime.textContent = `−${remainingFormatted}`;
    }
    
    // Depth metrics tracking
    trackDepthMetrics(audioEl);
    
    // Check for first listen completion
    checkFirstListenCompletion(audioEl);
  };
  audioEl.addEventListener('timeupdate', timeUpdateHandler);
  (audioEl as ExtendedHTMLAudioElement)._playerEventListeners!['timeupdate'] = timeUpdateHandler;
  
  // Metadata loaded event handler
  const metadataHandler = () => {
    // Initialize remaining time display
    if (audioEl.duration && !isNaN(audioEl.duration)) {
      const remainingFormatted = formatTime(audioEl.duration);
      remainingTime.textContent = `−${remainingFormatted}`;
    }
    
    // Initialize progress bar
    updatePlaybackProgressBar(audioEl);
  };
  audioEl.addEventListener('loadedmetadata', metadataHandler);
  (audioEl as ExtendedHTMLAudioElement)._playerEventListeners!['loadedmetadata'] = metadataHandler;
  
  // Audio ended event handler
  const endedHandler = () => {
    updatePlayPauseState(false);
    stopProgressAnimation(); // Stop RAF animation
    
    // Show full duration in elapsed time and zero in remaining
    elapsedTime.textContent = formatTime(audioEl.duration);
    remainingTime.textContent = '−00:00';
    
    // Set progress bar to 100% on ended
    updatePlaybackProgressBar(audioEl, true);
    
    // Log playback_ended event
    logEvent(EVENT_TYPES['PLAYBACK_ENDED']!, { 
      clipId: appState.activeClipId,
      duration: audioEl.duration
    });
  };
  audioEl.addEventListener('ended', endedHandler);
  (audioEl as ExtendedHTMLAudioElement)._playerEventListeners!['ended'] = endedHandler;
  
  // Play event handler
  const playHandler = () => {
    updatePlayPauseState(true);
    startProgressAnimation(audioEl); // Start RAF animation
  };
  audioEl.addEventListener('play', playHandler);
  (audioEl as ExtendedHTMLAudioElement)._playerEventListeners!['play'] = playHandler;
  
  // Pause event handler
  const pauseHandler = () => {
    updatePlayPauseState(false);
    stopProgressAnimation(); // Stop RAF animation
  };
  audioEl.addEventListener('pause', pauseHandler);
  (audioEl as ExtendedHTMLAudioElement)._playerEventListeners!['pause'] = pauseHandler;
  
  // Error event handler
  const errorHandler = (e: Event): void => {
    logger.warn('Audio error', e);
    const audioControls = document.querySelector('.audio-controls');
    if (audioControls) {
      audioControls.classList.add('audio-disabled');
    }
  };
  audioEl.addEventListener('error', errorHandler);
  (audioEl as ExtendedHTMLAudioElement)._playerEventListeners!['error'] = errorHandler;
  
  // Global keyboard handlers (attached only once)
  if (!globalSpaceHandlerAttached) {
    const globalKeyHandler = (e: KeyboardEvent): void => {
      // Don't fire shortcuts when focus is inside input/textarea/contenteditable
      const activeEl = document.activeElement;
      const isEditable = activeEl && (
        activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'TEXTAREA' ||
        (activeEl as HTMLElement).isContentEditable
      );
      
      if (isEditable) {
        return;
      }
      
      // Handle different key combinations
      const isSpace = e.key === ' ' && !e.repeat;
      const isJ = e.key === 'j' || e.key === 'J';
      const isK = e.key === 'k' || e.key === 'K';
      const isL = e.key === 'l' || e.key === 'L';
      const isArrowLeft = e.key === 'ArrowLeft' && e.altKey;
      const isArrowRight = e.key === 'ArrowRight' && e.altKey;
      
      // Only trigger if focus is on body or player container, or if using J/K/L shortcuts
      const isGlobalContext = document.activeElement === document.body || 
                              containerEl.contains(document.activeElement);
      
      // Space key: Play/Pause (only in global context)
      if (isSpace && isGlobalContext) {
        e.preventDefault();
        playPauseHandler();
      }
      
      // J or Option+ArrowLeft: Rewind 10s
      if (isJ || isArrowLeft) {
        e.preventDefault();
        rewindHandler();
      }
      
      // K: Play/Pause
      if (isK) {
        e.preventDefault();
        playPauseHandler();
      }
      
      // L or Option+ArrowRight: Forward 10s
      if (isL || isArrowRight) {
        e.preventDefault();
        forwardHandler();
      }
    };
    
    document.addEventListener('keydown', globalKeyHandler);
    globalSpaceHandlerAttached = true;
  }
  
  // TODO: Phase F - Add buffering indicator to show loading progress
}
